import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./StoreLayout";
import { useAuth } from "@/context/AuthContext";
import { api, extractError } from "@/lib/api";
import { formatIDR, unitPrice } from "@/lib/format";
import { ChevronLeft, MapPin, Truck, Tag, Wallet, QrCode, Landmark, HandCoins, Upload } from "lucide-react";
import { toast } from "sonner";
import AddressRegionFields from "@/components/kiyana/AddressRegionFields";

export default function StoreCheckout() {
  const { cart, setCart } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [addr, setAddr] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiyana_addr") || "null"); } catch { return null; }
  });
  const [editingAddr, setEditingAddr] = useState(!addr);
  const [courier, setCourier] = useState("reguler");
  const [rate, setRate] = useState(null);
  const [rateOpts, setRateOpts] = useState(null);
  const totalQty = cart.reduce((s, x) => s + x.qty, 0);
  useEffect(() => {
    if (!addr) return;
    setRateOpts(null);
    api.post("/shipping/rates", {
      province: addr?.province || "", city: addr?.city || "", district: addr?.district || "",
      postal_code: addr?.postal_code || "", weight_grams: Math.max(1, totalQty) * 400,
    }).then((r) => {
      setRateOpts(r.data);
      setRate(r.data.options?.[1] || r.data.options?.[0] || null);
    }).catch(() => setRateOpts({ options: [], mock: true }));
  }, [addr, totalQty]);
  const [payment, setPayment] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiyana_checkout_draft") || "null")?.payment || "manual_transfer"; } catch { return "manual_transfer"; }
  });
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [vouchersAvail, setVouchersAvail] = useState([]);
  const [voucherRemoved, setVoucherRemoved] = useState(false);
  const [note, setNote] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiyana_checkout_draft") || "null")?.note || ""; } catch { return ""; }
  });
  useEffect(() => {
    localStorage.setItem("kiyana_checkout_draft", JSON.stringify({ payment, note }));
  }, [payment, note]);
  const [busy, setBusy] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [region, setRegion] = useState(null);
  const [codCfg, setCodCfg] = useState(null);
  const [cod, setCod] = useState({ ktp_name: "", ktp_number: "", ktp_phone: "", agreed: false });
  const [codFile, setCodFile] = useState(null);
  useEffect(() => { api.get("/store/config").then((r) => setCodCfg(r.data)).catch(() => {}); }, []);

  const loadAddresses = () => {
    if (user) api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  };
  useEffect(() => { loadAddresses(); }, [user]);
  useEffect(() => {
    const def = addresses.find((a) => a.is_default) || addresses[0];
    if (def && !addr) {
      setAddr({ name: def.name, phone: def.phone, address: def.address });
      setEditingAddr(false);
    }
  }, [addresses]);

  const subtotal = useMemo(() => cart.reduce((s, x) => s + x.price * x.qty, 0), [cart]);
  const subsidy = subtotal >= 150000 ? 15000 : 0;
  const shipping = rate ? Math.max(0, rate.cost_idr - subsidy) : 0;
  const discount = voucher?.discount || 0;
  const codFee = payment === "cod" && codCfg?.cod_enabled ? Math.round(subtotal * (codCfg.cod_fee_percent || 0) / 100) : 0;
  const total = Math.max(0, subtotal + shipping - discount) + codFee;
  const codOverLimit = payment === "cod" && !!codCfg?.cod_max_total && (subtotal + shipping - discount) > codCfg.cod_max_total;

  useEffect(() => { api.get("/vouchers").then((r) => setVouchersAvail(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const claimed = localStorage.getItem("kiyana_claimed_voucher");
    if (!claimed) return;
    localStorage.removeItem("kiyana_claimed_voucher");
    api.get("/vouchers/validate", { params: { code: claimed, subtotal } }).then(({ data }) => {
      if (data.valid) {
        setVoucher({ ...data, auto: false });
        setVoucherRemoved(true);
        toast.success(`Voucher ${claimed} aktif dari halaman produk`);
      }
    }).catch(() => {});
  }, []);
  const voucherInfo = vouchersAvail.map((v) => {
    const eligible = subtotal >= (v.min_purchase || 0);
    const disc = eligible ? Math.min(Math.floor(subtotal * (v.percent || 0) / 100), v.max_discount || Infinity) : 0;
    return { ...v, eligible, discount: disc, kurang: Math.max(0, (v.min_purchase || 0) - subtotal) };
  });
  useEffect(() => {
    if (voucher && !voucher.auto) return;
    if (!voucher && voucherRemoved) return;
    const best = voucherInfo.filter((v) => v.eligible).sort((a, b) => b.discount - a.discount)[0];
    if (!best) { if (voucher?.auto) setVoucher(null); return; }
    if (!voucher || voucher.code !== best.code || voucher.discount !== best.discount) {
      setVoucher({ code: best.code, discount: best.discount, auto: true });
    }
  }, [subtotal, vouchersAvail]);

  const applyVoucher = async () => {
    if (!voucherInput.trim()) return;
    try {
      const { data } = await api.get("/vouchers/validate", { params: { code: voucherInput.trim(), subtotal } });
      if (data.valid) {
        setVoucher({ ...data, auto: false });
        setVoucherRemoved(false);
        toast.success(data.message);
      } else {
        setVoucher(null);
        toast.error(data.message);
      }
    } catch (e) { toast.error(extractError(e)); }
  };

  const pickVoucher = (v) => {
    setVoucher({ code: v.code, discount: v.discount, auto: false });
    setVoucherRemoved(false);
    toast.success(`Voucher ${v.code} dipakai — hemat ${formatIDR(v.discount)}`);
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherRemoved(true);
  };

  const saveAddr = (e) => {
    e.preventDefault();
    if (!region?.complete) return toast.error("Lengkapi wilayah alamat sampai kelurahan + detail jalan");
    const fd = new FormData(e.target);
    const a = {
      name: fd.get("name"), phone: fd.get("phone"), address: region.composed,
      province: region.province, city: region.city, district: region.district,
      village: region.village, postal_code: region.postal_code,
    };
    localStorage.setItem("kiyana_addr", JSON.stringify(a));
    setAddr(a);
    setEditingAddr(false);
    if (user?.role === "pelanggan") {
      api.post("/addresses", { ...a, is_default: addresses.length === 0 }).then(loadAddresses).catch(() => {});
    }
  };

  const bayar = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    if (!addr) { setEditingAddr(true); return toast.error("Isi alamat pengiriman dulu ya"); }
    if (!rate) return toast.error("Pilih kurir pengiriman dulu");
    if (!user || user.role !== "pelanggan") {
      toast.info("Silakan masuk sebagai pelanggan dulu");
      nav("/store/auth");
      return;
    }
    if (payment === "cod") {
      if (user?.cod_blocked) return toast.error("Akun Anda diblokir dari metode COD");
      if (codOverLimit) return toast.error(`COD maksimal total ${formatIDR(codCfg.cod_max_total)}`);
      if (cod.ktp_name.trim().length < 3) return toast.error("Isi nama sesuai KTP/Paspor");
      if (cod.ktp_number.trim().length < 8) return toast.error("Nomor KTP/Paspor tidak valid (min. 8 karakter)");
      if (cod.ktp_phone.trim().length < 9) return toast.error("No. HP aktif tidak valid");
      if (!codFile) return toast.error("Unggah foto KTP/Paspor dulu");
      if (!cod.agreed) return toast.error("Centang persetujuan perjanjian COD dulu");
    }
    setBusy(true);
    try {
      let cod_form = null;
      if (payment === "cod") {
        const fd = new FormData();
        fd.append("file", codFile);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        cod_form = { ktp_name: cod.ktp_name.trim(), ktp_number: cod.ktp_number.trim(), ktp_phone: cod.ktp_phone.trim(), ktp_image_url: up.data.url, agreed: true };
      }
      const { data } = await api.post("/orders", {
        items: cart.map((x) => ({ product_id: x.id, variant: x.variant, qty: x.qty, price: x.price })),
        address: `${addr.name} • ${addr.phone} • ${addr.address}`,
        courier: rate?.name || "JNE Reguler",
        courier_code: rate?.courier || null,
        service_code: rate?.service || null,
        destination: addr ? { province: addr.province || "", city: addr.city || "", district: addr.district || "", postal_code: addr.postal_code || "" } : null,
        payment_method: payment,
        voucher_code: voucher?.code || null,
        note,
        cod_form,
      });
      setCart([]);
      localStorage.removeItem("kiyana_checkout_draft");
      toast.success(`Pesanan ${data.code} dibuat — selesaikan pembayaranmu`);
      nav(`/store/order/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-up pb-6">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Checkout</h1>
        <div className="w-9" />
      </div>

      {/* Alamat */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> Alamat Pengiriman</div>
          {addr && !editingAddr && <button data-testid="addr-edit" onClick={() => setEditingAddr(true)} className="text-xs text-brand font-medium">Ubah</button>}
        </div>
        {!editingAddr && addresses.length > 0 && (
          <div className="space-y-2">
            {addresses.map((a) => (
              <button key={a.id} data-testid={`co-addr-${a.id}`} onClick={() => setAddr({ name: a.name, phone: a.phone, address: a.address })}
                className={`w-full p-3 rounded-xl border text-left text-xs ${addr?.address === a.address && addr?.phone === a.phone ? "border-brand bg-blush" : "border-blush-soft"}`}>
                <div className="font-semibold flex items-center gap-2">{a.name} {a.is_default && <span className="text-[9px] bg-brand text-white rounded-full px-1.5 py-0.5">Utama</span>}</div>
                <div className="text-cocoa-muted">{a.phone}</div>
                <div className="mt-0.5">{a.address}</div>
              </button>
            ))}
            <button data-testid="co-addr-new" onClick={() => setEditingAddr(true)} className="text-xs text-brand font-medium">+ Tambah alamat baru</button>
          </div>
        )}
        {editingAddr ? (
          <form onSubmit={saveAddr} className="space-y-2">
            <input name="name" data-testid="addr-name" defaultValue={addr?.name || user?.name || ""} placeholder="Nama penerima" required className="w-full h-10 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
            <input name="phone" data-testid="addr-phone" defaultValue={addr?.phone || ""} placeholder="No. HP (08xx)" required className="w-full h-10 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
            <AddressRegionFields testPrefix="addr" onChange={setRegion} />
            <button type="submit" data-testid="addr-save" className="w-full h-10 rounded-full bg-brand text-white text-sm font-semibold">Simpan Alamat</button>
          </form>
        ) : addresses.length === 0 && addr ? (
          <div className="text-xs text-cocoa leading-relaxed">
            <div className="font-semibold">{addr.name} <span className="font-normal text-cocoa-muted">({addr.phone})</span></div>
            <div className="text-cocoa-muted">{addr.address}</div>
          </div>
        ) : null}
      </div>

      {/* Kurir */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="text-sm font-semibold flex items-center gap-2 mb-1"><Truck className="w-4 h-4 text-brand" /> Kurir & Ongkir</div>
        <div className="text-[11px] text-cocoa-muted mb-3">Dikirim dari Kota Cirebon • subsidi ongkir Rp15.000 untuk belanja ≥ Rp150.000</div>
        {rateOpts === null && <div className="text-xs text-cocoa-muted py-3 text-center">Menghitung ongkir...</div>}
        {rateOpts?.mock && rateOpts?.options?.length > 0 && (
          <div className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-2.5 py-1.5 mb-2">Mode simulasi — tarif live aktif setelah API key Biteship diisi di Pengaturan admin.</div>
        )}
        <div className="space-y-2">
          {rateOpts?.options?.map((r) => {
            const finalPrice = Math.max(0, r.cost_idr - subsidy);
            const active = rate?.courier === r.courier && rate?.service === r.service;
            return (
              <button key={r.courier + r.service} data-testid={`courier-${r.courier}-${r.service}`} onClick={() => setRate(r)}
                className={`w-full p-3 rounded-xl border flex items-center justify-between text-sm ${active ? "border-brand bg-blush" : "border-blush-soft"}`}>
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? "border-brand bg-brand" : "border-blush-soft"}`} />
                  <span className="text-left min-w-0">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.courier === "jne" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-600"}`}>{(r.courier || "").toUpperCase()}</span>
                    <span className="ml-1.5 font-medium">{r.service}</span>
                    <div className="text-[10px] text-cocoa-muted mt-0.5">Estimasi tiba {r.etd}</div>
                  </span>
                </span>
                <span className="text-right flex-shrink-0">
                  {finalPrice === 0 && r.cost_idr > 0 && <span className="block text-[10px] text-cocoa-muted line-through">{formatIDR(r.cost_idr)}</span>}
                  <span className={finalPrice === 0 ? "text-emerald-600 font-semibold" : "font-semibold"}>{finalPrice === 0 ? "Gratis" : formatIDR(finalPrice)}</span>
                </span>
              </button>
            );
          })}
          {rateOpts?.options?.length === 0 && <div className="text-xs text-cocoa-muted text-center py-3">Layanan kurir tidak tersedia untuk rute ini.</div>}
        </div>
      </div>

      {/* Voucher */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Tag className="w-4 h-4 text-brand" /> Voucher</div>
        <div className="flex gap-2">
          <input data-testid="voucher-input" value={voucherInput} onChange={(e) => setVoucherInput(e.target.value.toUpperCase())} placeholder="Masukkan kode (mis. KIYANA10)" className="flex-1 h-10 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand font-mono" />
          <button data-testid="voucher-apply" onClick={applyVoucher} className="h-10 px-5 rounded-full bg-brand text-white text-sm font-semibold">Pakai</button>
        </div>
        {voucher && (
          <div className="mt-2 flex items-center justify-between gap-2 text-xs">
            <span className="text-emerald-600 font-medium">
              ✓ {voucher.code} terpasang — hemat {formatIDR(voucher.discount)}
              {voucher.auto && <span className="ml-1.5 text-[9px] bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5 font-semibold">Otomatis</span>}
            </span>
            <button data-testid="voucher-remove" onClick={removeVoucher} className="text-red-500 font-medium">Lepas</button>
          </div>
        )}
        {voucherInfo.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blush-soft space-y-2">
            <div className="text-[11px] font-semibold text-cocoa-muted">Voucher tersedia untukmu:</div>
            {voucherInfo.map((v) => {
              const active = voucher?.code === v.code;
              return (
                <div key={v.code} data-testid={`voucher-card-${v.code}`}
                  className={`rounded-xl border p-3 flex items-center gap-3 ${active ? "border-brand bg-blush" : v.eligible ? "border-blush-soft" : "border-blush-soft opacity-70"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-bold text-xs text-brand">{v.code}</span>
                      <span className="text-[10px] font-semibold text-cocoa">{v.percent}% OFF</span>
                    </div>
                    <div className="text-[10px] text-cocoa-muted mt-0.5">
                      {v.title} • Min. belanja {formatIDR(v.min_purchase)} • Maks. potongan {formatIDR(v.max_discount)}
                    </div>
                    {!v.eligible && (
                      <div className="text-[10px] text-amber-600 mt-0.5">Belanja {formatIDR(v.kurang)} lagi untuk memakai voucher ini</div>
                    )}
                  </div>
                  {active ? (
                    <span data-testid={`voucher-active-${v.code}`} className="text-[10px] font-bold text-emerald-600 flex-shrink-0">Terpakai ✓</span>
                  ) : v.eligible ? (
                    <button data-testid={`voucher-pick-${v.code}`} onClick={() => pickVoucher(v)} className="h-8 px-4 rounded-full bg-brand text-white text-[11px] font-semibold flex-shrink-0">Pakai</button>
                  ) : (
                    <span className="text-[10px] text-cocoa-muted flex-shrink-0">Belum memenuhi</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pembayaran */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="text-sm font-semibold flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-brand" /> Metode Pembayaran</div>
        <div className="space-y-2">
          {[
            { id: "manual_transfer", icon: Landmark, name: "Transfer Bank (Manual)", desc: "BCA / Mandiri a.n. Kiyana by Cirebon Cloth" },
            { id: "manual_qris", icon: QrCode, name: "QRIS Kiyana", desc: "Scan QRIS lalu upload bukti bayar" },
            { id: "xendit_va", icon: Wallet, name: "Virtual Account (Xendit)", desc: "Verifikasi otomatis — Mode Test" },
            ...(codCfg?.cod_enabled && !user?.cod_blocked ? [{ id: "cod", icon: HandCoins, name: "COD (Bayar di Tempat)", desc: `Fee ${codCfg.cod_fee_percent}% dari total belanja • wajib formulir perjanjian + KTP/Paspor` }] : []),
          ].map((m) => (
            <button key={m.id} data-testid={`pay-${m.id}`} onClick={() => setPayment(m.id)}
              className={`w-full p-3 rounded-xl border flex items-center gap-3 text-left ${payment === m.id ? "border-brand bg-blush" : "border-blush-soft"}`}>
              <m.icon className="w-5 h-5 text-brand flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-[11px] text-cocoa-muted">{m.desc}</div>
              </div>
              <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${payment === m.id ? "border-brand bg-brand" : "border-blush-soft"}`} />
            </button>
          ))}
        </div>
        {codCfg?.cod_enabled && user?.cod_blocked && (
          <div data-testid="cod-blocked-note" className="mt-2 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            Akun Anda tidak dapat menggunakan COD karena pernah menolak paket COD.
          </div>
        )}
      </div>

      {/* Formulir COD */}
      {payment === "cod" && codCfg?.cod_enabled && !user?.cod_blocked && (
        <div data-testid="cod-form" className="bg-white rounded-2xl border border-blush-soft p-4 space-y-2.5">
          <div className="text-sm font-semibold flex items-center gap-2"><HandCoins className="w-4 h-4 text-brand" /> Formulir Perjanjian COD</div>
          <input data-testid="cod-ktp-name" value={cod.ktp_name} onChange={(e) => setCod({ ...cod, ktp_name: e.target.value })} placeholder="Nama lengkap sesuai KTP/Paspor" className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
          <input data-testid="cod-ktp-number" value={cod.ktp_number} onChange={(e) => setCod({ ...cod, ktp_number: e.target.value })} placeholder="Nomor KTP / Paspor" className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand font-mono" />
          <input data-testid="cod-ktp-phone" value={cod.ktp_phone} onChange={(e) => setCod({ ...cod, ktp_phone: e.target.value })} placeholder="No. HP aktif (WhatsApp)" className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
          <label data-testid="cod-ktp-file-btn" className="w-full h-16 rounded-xl border-2 border-dashed border-blush-soft flex items-center justify-center gap-2 text-cocoa-muted cursor-pointer">
            <Upload className="w-4 h-4" />
            <span className="text-[11px]">{codFile ? codFile.name : "Unggah foto KTP/Paspor (JPG/PNG, maks 5MB)"}</span>
            <input data-testid="cod-ktp-file" type="file" accept="image/*" className="hidden" onChange={(e) => {
              const f = e.target.files[0];
              if (f && f.size > 5 * 1024 * 1024) { toast.error("Foto maksimal 5MB"); e.target.value = ""; return; }
              setCodFile(f || null);
            }} />
          </label>
          <label className="flex items-start gap-2.5 text-[11px] text-cocoa leading-relaxed cursor-pointer">
            <input data-testid="cod-agree" type="checkbox" checked={cod.agreed} onChange={(e) => setCod({ ...cod, agreed: e.target.checked })} className="mt-0.5 accent-pink-600" />
            <span>Saya menyetujui perjanjian COD: bersedia membayar tunai sebesar total tagihan (termasuk fee COD {codCfg.cod_fee_percent}%) kepada kurir saat paket tiba, dan bersedia akun dibekukan jika menolak paket tanpa alasan yang sah.</span>
          </label>
          {codOverLimit && <div data-testid="cod-overlimit" className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">Total melebihi batas COD {formatIDR(codCfg.cod_max_total)} — silakan pilih metode pembayaran lain.</div>}
        </div>
      )}

      {/* Catatan */}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan untuk penjual (opsional)" className="w-full h-11 px-4 rounded-2xl border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand" />

      {/* Ringkasan */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-cocoa-muted">Subtotal ({cart.length} produk)</span><span>{formatIDR(subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-cocoa-muted">Ongkir{rate ? ` (${rate.name})` : ""}</span><span>{rate ? (shipping === 0 ? "Gratis" : formatIDR(shipping)) : "Pilih kurir"}</span></div>
        {subsidy > 0 && rate && rate.cost_idr > shipping && <div className="flex justify-between text-emerald-600"><span>Subsidi ongkir</span><span>-{formatIDR(Math.min(subsidy, rate.cost_idr))}</span></div>}
        {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Diskon voucher</span><span>-{formatIDR(discount)}</span></div>}
        {codFee > 0 && <div className="flex justify-between"><span className="text-cocoa-muted">Fee COD ({codCfg.cod_fee_percent}%)</span><span>{formatIDR(codFee)}</span></div>}
        <div className="flex justify-between font-bold pt-2 border-t border-blush-soft"><span>Total Pembayaran</span><span className="text-brand" data-testid="checkout-total">{formatIDR(total)}</span></div>
      </div>

      <button
        data-testid="checkout-pay-btn"
        onClick={bayar}
        disabled={busy || cart.length === 0 || !rate || codOverLimit}
        className="w-full h-13 py-4 rounded-full bg-brand text-white font-semibold disabled:opacity-40"
      >
        {busy ? "Memproses..." : payment === "cod" ? "Buat Pesanan COD" : "Lanjut ke Pembayaran"}
      </button>
    </div>
  );
}
